/**
 * Small shared helpers for the SecurOne views: icon mapping, initials, date
 * formatting, scope labels and the error-to-text mapping.
 */

// Category icon keys stored by the SecurOne app (Lucide names, the same six
// the phone offers), mapped to Font Awesome 5 solid for the desktop.
export const CATEGORY_ICONS = {
  flame: "fire",
  siren: "bell",
  "heart-pulse": "heartbeat",
  "shield-alert": "shield-alt",
  "triangle-alert": "exclamation-triangle",
  megaphone: "bullhorn",
  "": "bell",
};

// The keys offered in the editor - exactly the phone's set, in the same order.
export const CATEGORY_ICON_KEYS = ["flame", "siren", "heart-pulse", "shield-alert", "triangle-alert", "megaphone"];

export function categoryIcon(key) {
  return CATEGORY_ICONS[key || ""] || "bell";
}

export function initials(name) {
  return String(name || "")
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((w) => w[0].toUpperCase())
    .join("") || "?";
}

function pad2(n) {
  return (n < 10 ? "0" : "") + n;
}

export function elapsed(from, to = Date.now()) {
  const seconds = Math.max(0, Math.floor((new Date(to).getTime() - new Date(from).getTime()) / 1000));
  const h = Math.floor(seconds / 3600), m = Math.floor((seconds % 3600) / 60), s = seconds % 60;
  return h > 0 ? `${h}:${pad2(m)}:${pad2(s)}` : `${pad2(m)}:${pad2(s)}`;
}

export function clock(t) {
  if (!t) return "—";
  const d = new Date(t);
  return `${pad2(d.getHours())}:${pad2(d.getMinutes())}`;
}

export function dateStamp(t) {
  if (!t) return "—";
  const d = new Date(t);
  return `${d.getFullYear()}-${pad2(d.getMonth() + 1)}-${pad2(d.getDate())}`;
}

export function dateTime(t) {
  return t ? `${dateStamp(t)} ${clock(t)}` : "—";
}

export function fileSize(bytes) {
  if (!bytes && bytes !== 0) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
}

// Ticks once a second so elapsed timers in templates stay live.
export const tickerMixin = {
  data() {
    return { now: Date.now(), _ticker: null };
  },
  created() {
    this._ticker = setInterval(() => {
      this.now = Date.now();
    }, 1000);
  },
  beforeDestroy() {
    clearInterval(this._ticker);
  },
  methods: {
    elapsedSince(from) {
      return elapsed(from, this.now);
    },
    clock,
    dateStamp,
    dateTime,
  },
};

// "Global" / "Whole customer" / "All users" / the department names, for a scope chip.
export function scopeLabel(vm, scope, departmentNames) {
  if (scope === "departments") return (departmentNames || []).join(", ") || vm.$t("securone.scope.departments");
  return vm.$t("securone.scope." + scope);
}

// Human error for the toasts: prefer the server's errorId (translated), then its message.
export function errorMessage(vm, error, fallbackKey = "securone.errors.generic") {
  const data = error && error.response && error.response.data ? error.response.data : null;
  if (data && data.errorId) {
    const key = `securone.errors.${data.errorId}`;
    if (vm.$te(key)) return vm.$t(key);
    if (data.message) return data.message;
  }
  return vm.$t(fallbackKey);
}

export function escapeHtml(s) {
  return String(s || "").replace(/&/g, "&amp;").replace(/</g, "&lt;").replace(/>/g, "&gt;");
}
